import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { websiteApi } from '../../../api/client';
import { ROUTES } from '../../../routes/constants';
import { WEBINAR_CONFIG } from './webinarConfig';

const BROKERS = ['FYERS', 'Zerodha', 'MasterTrust', 'Nuvama (join waitlist)', 'Other'];
const AUTOMATE_OPTIONS = ['Pine Script strategy', 'Indicator alerts', 'Options strategies', 'Not sure yet'];
const BROKERS_NEEDING_NOTE = new Set(['Nuvama (join waitlist)', 'Other']);

const schema = z.object({
  name: z.string().trim().min(2, 'Enter your name'),
  phone: z
    .string()
    .trim()
    .regex(/^\d{10}$/, 'Enter a valid 10-digit mobile number'),
  email: z.string().trim().min(1, 'Enter your email').email('Enter a valid email address'),
  broker: z.enum(BROKERS, { error: 'Select your broker' }),
  automate: z.string().optional(),
  consent: z.literal(true, { error: 'Consent is required to register' }),
  // Honeypot — real visitors never fill this in.
  website: z.string().optional(),
});

function buildMessage({ broker, automate, phone }) {
  return [
    `Webinar registration: ${WEBINAR_CONFIG.campaignTitle}`,
    'campaign_type: webinar',
    'lead_tag: webinar-registrant',
    `Broker: ${broker}`,
    `Automation interest: ${automate || 'Not specified'}`,
    `Mobile: ${phone}`,
    'Consent: User agreed to receive the webinar link and reminders on WhatsApp and email.',
  ].join(' | ');
}

export default function WebinarRegistrationForm() {
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState('');
  const formStartedAtRef = useRef(new Date().toISOString());

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      broker: '',
      automate: '',
      consent: false,
      website: '',
    },
  });

  const selectedBroker = watch('broker');

  const onSubmit = async (values) => {
    if (values.website) {
      // Honeypot tripped — silently drop, don't reveal the spam check to the caller.
      return;
    }
    setSubmitError('');
    try {
      // TODO: create a dedicated lead-capture schema later for funnel-specific fields
      // (campaign_type, lead_tag, automation_interest, consent) instead of packing them
      // into the generic `message` field.
      await websiteApi.submitContact({
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        broker: values.broker,
        message: buildMessage(values),
        source_page: 'tradingview-auto-trading-india',
        website: values.website || '',
        form_started_at: formStartedAtRef.current,
      });
      navigate(ROUTES.TRADINGVIEW_LANDING_THANK_YOU);
    } catch {
      // Don't surface raw backend/HTTP error text to visitors on a public funnel page.
      setSubmitError('We could not submit your registration right now. Please try again in a moment.');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="card p-6 md:p-8 space-y-5" noValidate>
      {/* Honeypot field — hidden from real visitors, never shown, never required. */}
      <div className="hidden" aria-hidden="true">
        <label htmlFor="tv-website">Leave this field empty</label>
        <input id="tv-website" type="text" tabIndex={-1} autoComplete="off" {...register('website')} />
      </div>

      <div>
        <label htmlFor="tv-name" className="field-label">Name</label>
        <input id="tv-name" type="text" className="input-field mt-1.5" placeholder="Your full name" {...register('name')} />
        {errors.name && <p className="mt-1 text-xs text-bear">{errors.name.message}</p>}
      </div>

      <div>
        <label htmlFor="tv-phone" className="field-label">WhatsApp / Mobile</label>
        <div className="mt-1.5 flex items-center gap-2">
          <span className="input-field flex w-16 flex-none items-center justify-center px-0">+91</span>
          <input
            id="tv-phone"
            type="tel"
            inputMode="numeric"
            maxLength={10}
            className="input-field"
            placeholder="10-digit mobile number"
            {...register('phone')}
          />
        </div>
        {errors.phone && <p className="mt-1 text-xs text-bear">{errors.phone.message}</p>}
      </div>

      <div>
        <label htmlFor="tv-email" className="field-label">Email</label>
        <input id="tv-email" type="email" className="input-field mt-1.5" placeholder="you@example.com" {...register('email')} />
        {errors.email && <p className="mt-1 text-xs text-bear">{errors.email.message}</p>}
      </div>

      <div>
        <label htmlFor="tv-broker" className="field-label">Your broker</label>
        <select id="tv-broker" className="input-field mt-1.5" defaultValue="" {...register('broker')}>
          <option value="" disabled>Select your broker</option>
          {BROKERS.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
        {errors.broker && <p className="mt-1 text-xs text-bear">{errors.broker.message}</p>}
        {BROKERS_NEEDING_NOTE.has(selectedBroker) && (
          <p className="mt-2 text-xs text-txt-muted">
            Support for your broker may be in testing or on our roadmap — we&apos;ll confirm on the call.
          </p>
        )}
      </div>

      <div>
        <label htmlFor="tv-automate" className="field-label">What do you want to automate?</label>
        <select id="tv-automate" className="input-field mt-1.5" defaultValue="" {...register('automate')}>
          <option value="">Not sure yet — I&apos;ll ask on the call</option>
          {AUTOMATE_OPTIONS.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </div>

      <div className="flex items-start gap-2.5">
        <input id="tv-consent" type="checkbox" className="mt-1 h-4 w-4 flex-none accent-amber" {...register('consent')} />
        <label htmlFor="tv-consent" className="text-sm text-txt-secondary">
          Send me the webinar link and reminders on WhatsApp and email.
        </label>
      </div>
      {errors.consent && <p className="text-xs text-bear">{errors.consent.message}</p>}

      {submitError && (
        <div className="rounded-md border border-bear/30 bg-bear/10 px-3 py-2 text-sm text-bear">
          {submitError}
        </div>
      )}

      <button type="submit" className="btn-primary w-full justify-center" disabled={isSubmitting}>
        {isSubmitting ? 'Registering…' : 'Register My Free Seat'}
      </button>

      <p className="text-center text-xs text-txt-muted">
        No spam. We&apos;ll contact you once to share your webinar joining details.
      </p>
    </form>
  );
}
